'use client';

import { useEffect, useRef, useState } from 'react';
import { UserPlus, BookOpen, Play, Trophy } from 'lucide-react';
import { SectionHeader, Card, PAGE_CONTAINER } from './ui';
import { useTranslations } from 'next-intl';

const STEP_ICONS = [UserPlus, BookOpen, Play, Trophy];
const STEP_NUMBERS = ['01', '02', '03', '04'];
const STEP_COLORS = ['bg-teal-800', 'bg-teal-800', 'bg-teal-900', 'bg-teal-900'];

export function HowItWorks() {
  const t = useTranslations('home.howItWorks');
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const steps = (t.raw('steps') as Array<{ title: string; description: string }>);

  useEffect(() => {
    // Four observers fire at different scroll depths: step activates when section
    // reaches 75%, 50%, 25%, 0% from the bottom of the viewport — no getBoundingClientRect needed.
    const margins = ['0px 0px -75% 0px', '0px 0px -50% 0px', '0px 0px -25% 0px', '0px 0px 0% 0px'];
    const observers = margins.map((rootMargin, step) => {
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveStep(prev => Math.max(prev, step));
        },
        { threshold: 0, rootMargin }
      );
      if (sectionRef.current) obs.observe(sectionRef.current);
      return obs;
    });
    return () => observers.forEach(obs => obs.disconnect());
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="py-10 lg:py-24 bg-pattern bg-teal-700 text-white overflow-hidden">
      <div className={PAGE_CONTAINER}>
        <SectionHeader
          badge={t('badge')}
          badgeVariant="dark"
          title={t('title')}
          description={t('description')}
          dark
          className="max-w-3xl mb-6 lg:mb-20"
        />

        {/* Mobile: compact vertical step list */}
        <div className="space-y-2 md:hidden">
          {steps.map((step, index) => {
            const Icon = STEP_ICONS[index];
            return (
              <div key={index} className="flex items-start gap-3 bg-teal-800/50 border border-teal-600 rounded-xl p-3">
                <div className={`w-8 h-8 rounded-lg ${STEP_COLORS[index]} flex items-center justify-center flex-shrink-0 font-black text-xs`}>
                  {STEP_NUMBERS[index]}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-sm text-white">{step.title}</h4>
                  <p className="text-teal-50 text-sm leading-relaxed mt-0.5">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: animated horizontal grid */}
        <div className="relative hidden md:block">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-teal-800 -translate-y-1/2">
            <div
              className="h-full bg-teal-300 transition-all duration-1000"
              style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const Icon = STEP_ICONS[index];
              const isActive = index <= activeStep;
              return (
                <div
                  key={index}
                  className={`relative transition-all duration-700 ${
                    isActive ? 'opacity-100 translate-y-0' : 'opacity-50 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className={`absolute -top-4 -left-2 w-10 h-10 rounded-xl ${STEP_COLORS[index]} flex items-center justify-center font-black text-sm shadow-lg z-10`}>
                    {STEP_NUMBERS[index]}
                  </div>
                  <Card variant="dark" padding="lg" className={`h-full transition-all bg-teal-800/50 border-teal-600 ${isActive ? 'ring-2 ring-teal-300/30' : ''}`}>
                    <div className={`w-14 h-14 rounded-2xl ${STEP_COLORS[index]} bg-opacity-20 flex items-center justify-center mb-5 ${isActive ? 'scale-110' : ''} transition-transform duration-500`}>
                      <Icon className={`w-7 h-7 ${isActive ? 'text-white' : 'text-teal-100'}`} />
                    </div>
                    <h4 className="text-lg font-bold mb-3">{step.title}</h4>
                    <p className="text-teal-50 text-sm leading-relaxed">{step.description}</p>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
